import React, { FunctionComponent } from "react";
import { Entry } from "contentful";

import {
  IContentSection,
  IContentSectionFields,
} from "@/types/generated/contentful";
import RichText from "../richtext";
import CallToAction from "../callToAction";
import Block from "../block";

type Props = {
  content: IContentSection;
};

const ContentSection: FunctionComponent<Props> = ({ content }) => {
  const {
    heading,
    headingStrongColor,
    body,
    callToAction,
    headingContent,
    headingContentAlignment,
    alignment,
    contentColumns,
    background,
    foreground,
    backgroundOverlayImage,
    backgroundOverlayType,
    removeTopPadding,
    removeBottomPadding,
  } = content.fields as IContentSectionFields;

  const alignClass = alignment === "left" ? "items-start" : "items-center";
  const gridCols = ["", "md:grid-cols-1", "md:grid-cols-2", "md:grid-cols-3"];
  const reversed = headingContentAlignment === "left";
  const padding = () => {
    const top = removeTopPadding ? "pt-0 xl:pt-0" : "pt-8 xl:pt-16";
    const bottom = removeBottomPadding ? "pb-0 xl:pb-0" : "pb-8 xl:pb-16";

    return `${top} ${bottom}`;
  };

  return (
    <section
      className={`mx-auto mb-auto flex w-screen flex-col gap-4 px-4 ${padding()}`}
      style={{
        backgroundColor: background || "transparent",
        ...(backgroundOverlayImage?.fields.file?.url && {
          backgroundImage:
            "url('" + backgroundOverlayImage?.fields.file?.url + "')",
        }),
        backgroundSize: backgroundOverlayType,
        ...(foreground && { color: foreground }),
      }}
    >
      <div
        className={`m-auto xl:container ${body && !!headingContent ? "grid grid-cols-1 items-center gap-8 md:grid-cols-2 lg:gap-16" : ""}`}
      >
        <div
          className={`flex flex-col ${alignClass} ${reversed ? "order-2" : "order-2 md:order-1"}`}
        >
          {heading && (
            <h2
              className={`mb-4 lg:mb-6 ${alignment === "center" ? "text-center" : "text-left"}`}
            >
              {
                <RichText
                  noContainers={true}
                  strongColor={headingStrongColor}
                  content={heading}
                />
              }
            </h2>
          )}
          {body && (
            <div
              className={`mb-4 ${alignment === "center" ? "text-center" : "text-left"} ${headingContent ? "xl:w-full" : "xl:w-3/4"}`}
            >
              {body && <RichText content={body} />}
            </div>
          )}
          {callToAction && (
            <div className="mb-4 flex flex-wrap items-center gap-4">
              {callToAction.map((action) => (
                <CallToAction content={action} key={action.sys.id} />
              ))}
            </div>
          )}
        </div>
        <div className={`${reversed ? "order-1" : "order-1 md:order-2"}`}>
          {headingContent && <Block block={headingContent as Entry<any>} />}
        </div>
      </div>
      {contentColumns && (
        <div
          className={`m-auto grid w-full grid-cols-1 justify-center gap-8 xl:container lg:gap-8 xl:px-0 ${gridCols[contentColumns.length]}`}
        >
          {contentColumns.map((block, index) => (
            <Block
              block={block as Entry<any>}
              key={index}
              contentSection={true}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default ContentSection;
